/**
 * licenseExcelExport — 29/09/2026 (demande user : « export en masse des
 * informations des licences en Excel dans la zone admin »).
 *
 * Feuille 1 « Licences » : une ligne par licence (clé, titulaire, email,
 * type, établissement, responsable pédagogique nom + clé, co-responsables,
 * propriétaire, dates, statut, IP, activations, dernière ouverture, REX,
 * notes). Feuille 2 « Synthèse » : compteurs par statut / type / cohorte.
 * Construction des lignes pure (testable) ; l'écriture xlsx est isolée.
 */
import { getLicenseStatus } from './adminLicenseFilters';

export const LICENSE_EXPORT_COLUMNS = [
  'Clé', 'Titulaire', 'Email', 'Type', 'Établissement', 'Responsable pédagogique', 'Clé responsable',
  'Co-responsables', 'Propriétaire', 'Créée le', 'Débute le', 'Expire le', 'Statut', 'IP max', 'IP uniques',
  'Activations', 'Dernière ouverture', 'REX demandé', 'Notes',
];

const STATUS_LABELS = { active: 'Active', expired: 'Expirée', disabled: 'Désactivée' };

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
};
const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('fr-FR');
};

export function buildLicenseRows(list, allLicenses = list, now = new Date()) {
  const byId = new Map((allLicenses || []).map((l) => [l.id, l]));
  return (list || []).map((lic) => {
    const master = lic.master_license_id ? byId.get(lic.master_license_id) : null;
    const coSup = (lic.co_supervisors || []).map((id) => byId.get(id)?.holder_name || id).join(', ');
    return {
      'Clé': lic.license_key || '',
      'Titulaire': lic.holder_name || '',
      'Email': lic.holder_email || lic.contact_email || '',
      'Type': lic.is_master ? 'Formateur' : 'Apprenant',
      'Établissement': lic.establishment || master?.establishment || '',
      'Responsable pédagogique': master?.holder_name || '',
      'Clé responsable': master?.license_key || '',
      'Co-responsables': coSup,
      'Propriétaire': lic.owner || '',
      'Créée le': fmtDate(lic.created_at),
      'Débute le': fmtDate(lic.starts_at),
      'Expire le': fmtDate(lic.expires_at),
      'Statut': STATUS_LABELS[getLicenseStatus(lic, now)],
      'IP max': lic.max_ips ? lic.max_ips : 'Illimité',
      'IP uniques': Array.isArray(lic.unique_ips) ? lic.unique_ips.length : (lic.unique_ips ?? ''),
      'Activations': Array.isArray(lic.activations) ? lic.activations.length : (lic.activations ?? 0),
      'Dernière ouverture': fmtDateTime(lic.last_app_open || lic.last_activated_at),
      'REX demandé': lic.rex_requested ? 'Oui' : 'Non',
      'Notes': lic.notes || '',
    };
  });
}

export function buildLicenseSummary(list, allLicenses = list, now = new Date()) {
  const rows = [];
  const push = (section, label, value) => rows.push({ 'Rubrique': section, 'Libellé': label, 'Nombre': value });
  const statuses = { active: 0, expired: 0, disabled: 0 };
  (list || []).forEach((l) => { statuses[getLicenseStatus(l, now)] += 1; });
  push('Total', 'Licences exportées', (list || []).length);
  Object.entries(statuses).forEach(([k, v]) => push('Statut', STATUS_LABELS[k], v));
  push('Type', 'Formateurs', (list || []).filter((l) => l.is_master).length);
  push('Type', 'Apprenants', (list || []).filter((l) => !l.is_master).length);
  const byId = new Map((allLicenses || []).map((l) => [l.id, l]));
  const cohorts = new Map();
  (list || []).filter((l) => !l.is_master).forEach((l) => {
    const master = l.master_license_id ? byId.get(l.master_license_id) : null;
    const label = master ? `${master.holder_name}${master.establishment ? ` — ${master.establishment}` : ''}` : 'Sans responsable';
    cohorts.set(label, (cohorts.get(label) || 0) + 1);
  });
  [...cohorts.entries()].sort((a, b) => b[1] - a[1]).forEach(([label, n]) => push('Cohorte', label, n));
  return rows;
}

export function licenseExportFilename(scope, now = new Date()) {
  const stamp = now.toISOString().slice(0, 10);
  return `HST-licences-${scope === 'all' ? 'toutes' : 'filtrees'}-${stamp}.xlsx`;
}

const autoWidths = (rows, keys) => keys.map((k) => ({
  wch: Math.min(60, Math.max(k.length, ...rows.map((r) => String(r[k] ?? '').length)) + 2),
}));

export async function exportLicensesToExcel(list, { allLicenses, scope = 'filtered' } = {}) {
  const XLSX = await import('xlsx');
  const rows = buildLicenseRows(list, allLicenses || list);
  const summary = buildLicenseSummary(list, allLicenses || list);
  const wb = XLSX.utils.book_new();
  const ws1 = XLSX.utils.json_to_sheet(rows, { header: LICENSE_EXPORT_COLUMNS });
  ws1['!cols'] = autoWidths(rows, LICENSE_EXPORT_COLUMNS);
  ws1['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(LICENSE_EXPORT_COLUMNS.length - 1)}${Math.max(1, rows.length + 1)}` };
  XLSX.utils.book_append_sheet(wb, ws1, 'Licences');
  const ws2 = XLSX.utils.json_to_sheet(summary, { header: ['Rubrique', 'Libellé', 'Nombre'] });
  ws2['!cols'] = autoWidths(summary, ['Rubrique', 'Libellé', 'Nombre']);
  XLSX.utils.book_append_sheet(wb, ws2, 'Synthèse');
  const filename = licenseExportFilename(scope);
  XLSX.writeFile(wb, filename);
  return { filename, rows: rows.length };
}
